import React from 'react';
import styled from 'styled-components';
import TrustPromise from './TrustPromise';

const Shell = styled.div`
  min-height: 100vh;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vw, 2rem);

  @media (max-width: 768px) {
    align-items: flex-start;
    padding-top: 2rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.1);
  overflow: hidden;
  width: 100%;
  max-width: 1100px;
  display: flex;
  min-height: 560px;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
    border-radius: 16px;
  }
`;

const FormPanel = styled.div`
  flex: 1.05;
  padding: clamp(1.75rem, 5vw, 2.75rem) clamp(1.25rem, 4vw, 2rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  order: 1;

  @media (max-width: 768px) {
    order: 1;
  }
`;

const HighlightPanel = styled.aside`
  flex: 0.95;
  background: #0f766e;
  color: white;
  padding: clamp(1.75rem, 5vw, 2.75rem) clamp(1.25rem, 4vw, 2rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-width: 0;
  order: 2;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.12), transparent 45%),
      radial-gradient(circle at 80% 80%, rgba(15, 23, 42, 0.18), transparent 50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    order: 2;
  }
`;

const HighlightInner = styled.div`
  position: relative;
  z-index: 1;
`;

const HighlightTitle = styled.h1`
  margin: 0 0 0.75rem;
  font-size: clamp(1.6rem, 4vw, 2.15rem);
  font-weight: 700;
  line-height: 1.2;
`;

const HighlightTagline = styled.p`
  margin: 0 0 1.25rem;
  font-size: clamp(0.95rem, 2.4vw, 1.05rem);
  line-height: 1.5;
  opacity: 0.95;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 1.25rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const FeatureItem = styled.li`
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const FeatureIcon = styled.span`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
`;

const FeatureCopy = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-size: 0.95rem;
    margin-bottom: 0.2rem;
  }

  span {
    display: block;
    font-size: 0.85rem;
    line-height: 1.45;
    opacity: 0.92;
  }
`;

/**
 * Shared two-panel auth shell: form + demos on one side, highlights/taglines on the other.
 */
export default function AuthSplitShell({
  children,
  title,
  tagline,
  features = [],
  showTrust = true,
}) {
  return (
    <Shell>
      <Card>
        <FormPanel>{children}</FormPanel>
        <HighlightPanel>
          <HighlightInner>
            <HighlightTitle>{title}</HighlightTitle>
            {tagline ? <HighlightTagline>{tagline}</HighlightTagline> : null}
            {showTrust ? <TrustPromise tone="light" align="left" compact /> : null}
            {features.length > 0 ? (
              <FeatureList>
                {features.map((item) => (
                  <FeatureItem key={item.title}>
                    <FeatureIcon aria-hidden>{item.icon || '•'}</FeatureIcon>
                    <FeatureCopy>
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </FeatureCopy>
                  </FeatureItem>
                ))}
              </FeatureList>
            ) : null}
          </HighlightInner>
        </HighlightPanel>
      </Card>
    </Shell>
  );
}
